/**
 * Remembers extra-service order per tenant + professional + primary service.
 * Used so reception keeps the same sequence (babyliss before blow-dry, etc.).
 */
export const AGENDA_EXTRA_ORDER_BY_PAIR_KEY = "adatis.agenda.extraOrderByPair";

type PairOrderMap = Record<string, Record<string, number[]>>;

const pairKey = (professionalId: number, serviceId: number): string =>
  `${professionalId}:${serviceId}`;

const readMap = (): PairOrderMap => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const raw = localStorage.getItem(AGENDA_EXTRA_ORDER_BY_PAIR_KEY);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return parsed as PairOrderMap;
  } catch {
    return {};
  }
};

const writeMap = (map: PairOrderMap): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    localStorage.setItem(AGENDA_EXTRA_ORDER_BY_PAIR_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / private mode
  }
};

export const getExtraOrderPreference = (
  tenantId: number | null | undefined,
  professionalId: number | null | undefined,
  serviceId: number | null | undefined
): number[] | null => {
  if (
    tenantId == null ||
    !Number.isFinite(tenantId) ||
    professionalId == null ||
    !Number.isFinite(professionalId) ||
    serviceId == null ||
    !Number.isFinite(serviceId)
  ) {
    return null;
  }

  const ids = readMap()[String(tenantId)]?.[pairKey(professionalId, serviceId)];

  if (!Array.isArray(ids) || ids.length === 0) {
    return null;
  }

  return ids.map(Number).filter((value) => Number.isFinite(value));
};

export const setExtraOrderPreference = (
  tenantId: number | null | undefined,
  professionalId: number | null | undefined,
  serviceId: number | null | undefined,
  orderedIds: number[]
): void => {
  if (
    tenantId == null ||
    !Number.isFinite(tenantId) ||
    professionalId == null ||
    !Number.isFinite(professionalId) ||
    serviceId == null ||
    !Number.isFinite(serviceId) ||
    orderedIds.length === 0
  ) {
    return;
  }

  const map = readMap();
  const tenantKey = String(tenantId);
  const tenantMap = { ...(map[tenantKey] ?? {}) };
  tenantMap[pairKey(professionalId, serviceId)] = orderedIds
    .map(Number)
    .filter((value) => Number.isFinite(value));
  map[tenantKey] = tenantMap;
  writeMap(map);
};
